import ApartmentRounded from "@mui/icons-material/ApartmentRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import EventRepeatRounded from "@mui/icons-material/EventRepeatRounded";
import KeyRounded from "@mui/icons-material/KeyRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import { Box, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { type StatusChecklistItem, SystemStatusChecklist } from "../../../../components/UIToolkit/molecules/SystemStatusChecklist";
import { useLastNameFirst } from "../../../../hooks/useNameFormat";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import type { NurseMergePreview } from "../../../../utils/nurseMergeModels";
import { MergeConfirmHero } from "./MergeConfirmHero";
import { formatNurseLabel, groupMovedCounts, type MergeChoices, removedOverlapShiftCount } from "./mergeLogic";
import type { MergeLookups } from "./useMergeDuplicateFlow";

type Props = {
	preview: NurseMergePreview;
	choices: MergeChoices;
	lookups: MergeLookups;
};

/** Step 4: exactly what the merge will do, in plain terms, before anything is written. */
export const MergeConfirmStep: FC<Props> = ({ preview, choices, lookups }) => {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const { groups, otherCount } = groupMovedCounts(preview.movedCounts);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }} data-testid="merge-employees-confirm-step">
			<MergeConfirmHero kept={preview.primary} deleted={preview.duplicate} groups={groups} otherCount={otherCount} />
			<SystemStatusChecklist
				title={t("nurse.merge.confirm.list.title")}
				items={buildConsequences(t, preview, choices, lookups, lastNameFirst)}
				dataTestId="merge-employees-consequences"
			/>
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("nurse.merge.confirm.no.undo")}</Typography>
		</Box>
	);
};

const buildConsequences = (
	t: TFunction,
	preview: NurseMergePreview,
	choices: MergeChoices,
	lookups: MergeLookups,
	lastNameFirst: boolean,
): StatusChecklistItem[] => {
	const names = { kept: formatNurseLabel(preview.primary, lastNameFirst), deleted: formatNurseLabel(preview.duplicate, lastNameFirst) };
	const items: StatusChecklistItem[] = [
		{
			id: "deleted",
			state: "active",
			icon: <DeleteOutlineRounded sx={{ color: "error.main" }} />,
			label: t("nurse.merge.confirm.deleted", names),
			dataTestId: "merge-employees-consequence-deleted",
		},
		{
			id: "login",
			state: "active",
			icon: <KeyRounded sx={{ color: "primary.main" }} />,
			label: t(`nurse.merge.login.plan.${preview.loginPlan}`, names),
			dataTestId: "merge-employees-consequence-login",
		},
	];

	if (preview.departmentConflicts.length > 0) {
		items.push({
			id: "departments",
			state: "active",
			icon: <ApartmentRounded sx={{ color: "primary.main" }} />,
			label: t("nurse.merge.confirm.departments", { name: names.kept }),
			description: preview.departmentConflicts
				.map((conflict) =>
					t(choices.keepActiveByDepartment[conflict.departmentId] ? "nurse.merge.confirm.department.active" : "nurse.merge.confirm.department.inactive", {
						department: lookups.departmentName(conflict.departmentId),
					}),
				)
				.join(" · "),
			dataTestId: "merge-employees-consequence-departments",
		});
	}

	for (const conflict of preview.recurringConflicts) {
		const selectedNurseId = choices.recurringKeepNurseIdByDepartment[conflict.departmentId];
		const selectedIsPrimary = selectedNurseId === preview.primary.nurseId;
		if (!selectedIsPrimary && selectedNurseId !== preview.duplicate.nurseId) {
			throw new Error(`Invalid recurring schedule choice for ${conflict.departmentId}`);
		}
		const selected = selectedIsPrimary ? preview.primary : preview.duplicate;
		const stopped = selectedIsPrimary ? preview.duplicate : preview.primary;
		const count = selectedIsPrimary ? conflict.primaryActiveCount : conflict.duplicateActiveCount;
		items.push({
			id: `recurring-${conflict.departmentId}`,
			state: "active",
			icon: <EventRepeatRounded sx={{ color: "primary.main" }} />,
			label: t("nurse.merge.confirm.recurring", {
				department: lookups.departmentName(conflict.departmentId),
				kept: formatNurseLabel(selected, lastNameFirst),
				stopped: formatNurseLabel(stopped, lastNameFirst),
				count,
			}),
			description: t("nurse.merge.confirm.recurring.created.shifts"),
			dataTestId: `merge-employees-consequence-recurring-${conflict.departmentId}`,
		});
	}

	if (preview.shiftOverlaps.length > 0 && choices.overlapKeepNurseId !== null) {
		const keptShifts = choices.overlapKeepNurseId === preview.primary.nurseId;
		items.push({
			id: "overlaps",
			state: "active",
			icon: <EventBusyRounded sx={{ color: "warning.main" }} />,
			label: t("nurse.merge.confirm.overlaps", {
				count: removedOverlapShiftCount(preview, choices.overlapKeepNurseId),
				removed: keptShifts ? names.deleted : names.kept,
			}),
			dataTestId: "merge-employees-consequence-overlaps",
		});
	}

	for (const warning of preview.warnings) {
		if (warning === "recurring_shifts_on_both") continue;
		items.push({
			id: `warning-${warning}`,
			state: "active",
			icon: <WarningAmberRounded sx={{ color: "warning.main" }} />,
			label: t(`nurse.merge.warning.${warning}`, names),
			dataTestId: `merge-employees-warning-${warning}`,
		});
	}
	return items;
};
